import type {
  AddOutcome,
  AddTeammateRequest,
  ArtifactState,
  AskEvent,
  AskRequest,
  Bookend as WireBookend,
  EditOutcome,
  Evidence,
  IssuedPairingCode,
  KnowledgeKind,
  LibraryItem,
  MemoryFactsPage,
  MemoryFactsQuery,
  MemoryTopics,
  PeopleRoster,
  PersonId,
  ReadingStep,
  RecordingDetail,
  RecordingId,
  RecordingSummary,
  SessionDerivedContent,
  SessionId,
  SessionSummary,
  SessionTranscript,
} from "@don/wire";

import type { KINDS } from "../knowledge/memory.js";
import type { SessionKnowledge } from "../knowledge/source.js";
import type { LibraryRow } from "../library/row.js";
import type { Bookend, SegmentReading, SessionReading, StepAt, SummaryStep } from "../session/reading.js";
import type { SessionEpoch, SessionMedia, SessionTrack } from "../session/timeline.js";

/**
 * The Console's one data source: the backend's owner routes, read over `fetch` (issue 90).
 *
 * Every request goes to `/api` on the Console's own origin, where the backend serves its
 * routes (`crates/backend/src/hosting.rs`, issue 139), and carries the owner-session cookie
 * because it is same-origin (issue 118, `auth/session.ts`). The dev server proxies that prefix
 * unchanged, and with `DON_DEV_OWNER` set adds the dev owner credential on the way
 * (`dev-origins.ts`), so nothing in this file, and nothing in a bundle built from it, holds a
 * credential. The
 * media is the same origin too: the playlist names `media.mp4` relative to itself, the
 * backend answers that with a redirect to a signed `GET` under `/media`, and the dev
 * server proxies that to the store. The backend gains no CORS.
 *
 * Pure maps plus one factory, so the mapping is asserted without a browser
 * (`adapter.test.ts`) and the screens stay unaware that a network exists.
 */
export const API_BASE = "/api";

/**
 * How many displays, as the library's shape column reads.
 *
 * `0` is a session that declared no media epoch, which has nothing to play. It is said
 * rather than drawn as `1 screen`.
 */
export function shapeLabel(displays: number): string {
  if (displays === 0) return "no screens";
  return displays === 1 ? "1 screen" : `${displays} screens`;
}

/** One session as a library row: a legacy item of `GET /recordings`, or a recording's segment (issue 321). The wire is snake_case (issue 84). */
export function libraryRowOf(summary: SessionSummary): LibraryRow {
  return {
    sessionId: summary.session_id,
    person: summary.person_name,
    startedAtMs: summary.started_at_ms,
    endedAtMs: summary.ended_at_ms,
    shape: shapeLabel(summary.shape),
    title: summary.title,
    summaryState: summary.summary_state,
    summaryAbsentReason: summary.summary_absent_reason,
    noteCount: summary.note_count,
    videoExpiresAtMs: summary.video_expires_at_ms,
    derivedExpiresAtMs: summary.derived_expires_at_ms,
  };
}

/**
 * Which state a recording's Summaries read as one (issue 321): `pending` while any segment's
 * is, else `present` if any is, else `failed` if any is, else `absent_by_design`. A row that
 * read `present` over a segment still being written would say the writing is done.
 */
function recordingState(states: readonly ArtifactState[]): ArtifactState {
  for (const state of ["pending", "present", "failed"] as const) {
    if (states.includes(state)) return state;
  }
  return "absent_by_design";
}

/**
 * A recording as one library row (issue 321, ADR 0051). `sessionId` carries the recording's
 * id, the row's one key (`data-library-row`, the open row, a renewal's state), and
 * `recordingId` says that is what it is. Retention on a recording is issue 322's: until then a
 * renewal or delete sent with this id reaches no session (the session routes answer 404).
 *
 * Start and end are the item's, which the route reads from the listed segments. The video goes
 * with the earliest-expiring segment (a recording's terms are counted together, issue 319, so
 * they agree); a segment's absence reason is not carried, since one reason does not speak for
 * several Summaries.
 */
function recordingRowOf(item: RecordingSummary): LibraryRow {
  const { segments } = item;
  const state = recordingState(segments.map((segment) => segment.summary_state));
  return {
    sessionId: item.recording_id,
    recordingId: item.recording_id,
    segments: segments.map(libraryRowOf),
    person: item.person_name,
    startedAtMs: item.started_at_ms,
    endedAtMs: item.ended_at_ms,
    shape: shapeLabel(Math.max(0, ...segments.map((segment) => segment.shape))),
    title: null,
    summaryState: state,
    summaryAbsentReason: null,
    noteCount: segments.reduce((total, segment) => total + segment.note_count, 0),
    videoExpiresAtMs: Math.min(...segments.map((segment) => segment.video_expires_at_ms)),
    derivedExpiresAtMs: Math.min(...segments.map((segment) => segment.derived_expires_at_ms)),
  };
}

/**
 * `GET /recordings` as the library's rows (issue 321): one per item, a closed recording or a
 * legacy session, so a recording is never split into its segments and a legacy session is
 * never hidden. Asserted by `adapter.test.ts`.
 */
export function libraryRowsOf(items: readonly LibraryItem[]): LibraryRow[] {
  return items.map((item) => (item.kind === "recording" ? recordingRowOf(item) : libraryRowOf(item)));
}

/** What the recording detail plays, reads and lists as knowledge (issue 321). */
export interface RecordingView {
  readonly media: SessionMedia;
  readonly reading: SessionReading;
  readonly knowledge: SessionKnowledge;
}

/** A place in a segment, moved onto the recording's timeline. A bookend has no time to move. */
function shifted(at: StepAt, offsetMs: number): StepAt {
  return "atMs" in at ? { atMs: at.atMs + offsetMs } : at;
}

/**
 * `GET /recordings/{id}` as one player, one reading column and one knowledge list (issue 321).
 *
 * **One timeline, laid out by the route's offsets.** Each segment's playlists start at its
 * `offset_ms`, which the route computes (issue 320) and the Console does not recompute; within
 * a segment a track's epochs follow each other as a session's do. Epochs are renumbered per
 * track in timeline order, because the player names the loaded epoch by `ordinal` and two
 * segments each have an epoch 0.
 *
 * The reading lists each segment's Summary in ordinal order with its range on that timeline
 * (`offset_ms` to `offset_ms + media_duration_ms`); its steps and its knowledge items' evidence
 * move by the same offset. No route answers a recording's Transcript, and always-on records no
 * speech, so it reads `absent_by_design`.
 */
export function recordingViewOf(detail: RecordingDetail, base = API_BASE): RecordingView {
  const tracks: SessionTrack[] = [];
  const epochs: SessionEpoch[] = [];
  const nextOrdinal = new Map<number, number>();
  let durationMs = 0;
  for (const segment of detail.segments) {
    durationMs = Math.max(durationMs, segment.offset_ms + segment.media_duration_ms);
    const startOf = new Map<number, number>();
    for (const playlist of segment.playlists) {
      const track = playlist.track_index;
      if (!tracks.some((each) => each.index === track)) {
        tracks.push({ index: track, label: `Screen ${track + 1}`, kind: "video" });
      }
      const startMs = startOf.get(track) ?? segment.offset_ms;
      const ordinal = nextOrdinal.get(track) ?? 0;
      epochs.push({
        trackIndex: track,
        ordinal,
        startMs,
        durationMs: playlist.media_duration_ms,
        mediaDurationMs: playlist.media_duration_ms,
        playlist: `${base}${playlist.path}`,
        width: 0,
        height: 0,
      });
      startOf.set(track, startMs + playlist.media_duration_ms);
      nextOrdinal.set(track, ordinal + 1);
    }
  }
  tracks.sort((a, b) => a.index - b.index);

  const segments: SegmentReading[] = detail.segments.map((segment) => ({
    ordinal: segment.ordinal,
    sessionId: segment.session.session_id,
    startMs: segment.offset_ms,
    endMs: segment.offset_ms + segment.media_duration_ms,
    summaryState: segment.summary_state.state,
    summaryAbsentReason: segment.summary_state.absent_reason,
    paragraph: segment.summary?.paragraph ?? null,
    steps: (segment.summary?.steps ?? []).map((step) => {
      const mapped = stepOf(step);
      return { ...mapped, id: `${segment.ordinal}-${mapped.id}`, at: shifted(mapped.at, segment.offset_ms) };
    }),
  }));

  const states = detail.segments.map((segment) => segment.summary_state.state);
  const answered = detail.segments.filter((segment) => segment.summary_state.state === "present" && segment.summary !== null);
  const knowledge: SessionKnowledge =
    answered.length > 0
      ? {
          state: "answered",
          items: answered.flatMap((segment) =>
            segment.summary!.knowledge.map((item) => ({
              id: `${segment.ordinal}-${item.id}`,
              kind: KIND_WORD[item.kind] ?? "Other",
              statement: item.statement,
              status: "draft" as const,
              evidence: [shifted(atOf(item.at), segment.offset_ms)],
            })),
          ),
          workflows: [],
        }
      : states.includes("pending")
        ? { state: "pending" }
        : states.includes("failed")
          ? { state: "failed" }
          : { state: "absent" };

  return {
    media: { sessionId: detail.recording_id, durationMs, tracks, epochs, gaps: [] },
    reading: {
      sessionId: detail.recording_id,
      durationMs,
      summaryState: recordingState(states),
      summaryAbsentReason: null,
      transcriptState: "absent_by_design",
      transcriptAbsentReason: null,
      intro: null,
      outro: null,
      entries: [],
      transcript: [],
      paragraph: null,
      steps: [],
      segments,
    },
    knowledge,
  };
}

/** A step as the port carries it: the wire's evidence without its `basis` tag. */
function stepOf(step: ReadingStep): SummaryStep {
  return { id: step.id, at: atOf(step.at), text: step.text };
}

/**
 * One bookend's transcript, as the `Bookend` port carries it: every line spoken in that clip,
 * in offset order, as one text. `null` where nothing was transcribed from it.
 *
 * The person named beside it is the session's (`transcriptRows`' `person`), never a name read
 * off the line: the wire's `speaker` is the session's person by construction (`wire::
 * TranscriptLine`), and the port has no person id to check it against.
 */
function bookendOf(transcript: SessionTranscript | null, which: WireBookend): Bookend | null {
  const lines = (transcript?.lines ?? [])
    .filter((line) => line.bookend === which)
    .sort((a, b) => a.offset_ms - b.offset_ms);
  if (lines.length === 0) return null;
  return { which, durationMs: null, text: lines.map((line) => line.text).join(" ") };
}

/**
 * The reading column's port, from the derived route (`GET /sessions/{id}/derived`, issue 228).
 *
 * The two states and their reasons; under a present Summary, its paragraph and steps; under a
 * present Transcript, its lines on the intro and outro. The route sends a document only under
 * `present` (`wire::SessionDerivedContent`). No route answers notes, voiceovers or
 * Observations, so `entries` is empty, and every transcript line is a bookend's, so the timed
 * `transcript` is empty too.
 */
export function sessionReadingOf(
  sessionId: SessionId,
  content: SessionDerivedContent,
  durationMs: number,
): SessionReading {
  const summary = content.summary_state;
  const transcript = content.transcript_state;
  return {
    sessionId,
    durationMs,
    summaryState: summary.state,
    summaryAbsentReason: summary.absent_reason,
    transcriptState: transcript.state,
    transcriptAbsentReason: transcript.absent_reason,
    intro: bookendOf(content.transcript, "intro"),
    outro: bookendOf(content.transcript, "outro"),
    entries: [],
    transcript: [],
    paragraph: content.summary?.paragraph ?? null,
    steps: (content.summary?.steps ?? []).map(stepOf),
  };
}

/**
 * Each wire kind's word, every one on the memory module's kind list (`KINDS`), `Other` included. The
 * Knowledge page's fact meta words a kind with it too (issue 279).
 */
export const KIND_WORD: Record<KnowledgeKind, (typeof KINDS)[number]> = {
  rule: "Rule",
  gotcha: "Gotcha",
  where_to_find: "Where to find",
  contact: "Contact",
  decision: "Decision",
  other: "Other",
};

/** A step's or an item's evidence as the port carries it: the wire's without its `basis` tag. */
export function atOf(at: Evidence): StepAt {
  return at.basis === "bookend" ? { bookend: at.bookend } : { atMs: at.media_ms };
}

/**
 * The session's knowledge, from the same derived route (issue 229). Knowledge is part of the
 * Summary document, so it takes the Summary's state: `pending` and `failed` as they are,
 * `absent_by_design` as `absent`, and `present` as the document's items. Every item is a draft
 * (no route records a keep), and the route answers no workflows.
 */
export function sessionKnowledgeOf(content: SessionDerivedContent): SessionKnowledge {
  const state = content.summary_state.state;
  if (state === "pending" || state === "failed") return { state };
  if (state === "absent_by_design" || content.summary === null) return { state: "absent" };
  return {
    state: "answered",
    items: content.summary.knowledge.map((item) => ({
      id: item.id,
      // `/derived` is cast, not validated: a kind the api adds before this Console ships reads
      // as `Other`, as the PRD's derivation reads an unknown model kind, rather than an empty label.
      kind: KIND_WORD[item.kind] ?? "Other",
      statement: item.statement,
      status: "draft",
      evidence: [atOf(item.at)],
    })),
    workflows: [],
  };
}

/**
 * The media one epoch's playlist holds, in milliseconds: the sum of its `EXTINF`s.
 *
 * Summed in whole milliseconds, because `registry::playlist` writes each fragment's
 * `media_duration_ms` as fixed-point seconds with three decimals and `timelineOf` refuses a
 * fractional millisecond.
 */
export function playlistMediaMs(text: string): number {
  let total = 0;
  for (const line of text.split("\n")) {
    const match = /^#EXTINF:([0-9.]+)/.exec(line.trim());
    if (match?.[1] !== undefined) total += Math.round(Number(match[1]) * 1000);
  }
  return total;
}

/** A read the backend refused or could not answer, with the status it gave. */
export class BackendError extends Error {
  constructor(
    readonly what: string,
    readonly status: number,
  ) {
    super(`${what} answered ${status}`);
    this.name = "BackendError";
  }
}

/** What the Console asks the backend. */
export interface Backend {
  library(): Promise<LibraryRow[]>;
  /** The Team table's roster, `GET /people` (issue 212). The screen orders it. */
  people(): Promise<PeopleRoster>;
  /** New pairing code, `POST /people/{id}/pairing-codes` (issue 214). Cancels nothing. */
  issuePairingCode(personId: PersonId): Promise<IssuedPairingCode>;
  /**
   * Add teammate, `POST /people` (issue 215): `Added` with the first code, `EmailOnTeam`, or
   * `EmailDeactivated` naming the deactivated person who holds the address (issue 218).
   */
  addTeammate(request: AddTeammateRequest): Promise<AddOutcome>;
  /** Edit, `POST /people/{id}/edit` (issue 216): `Edited` with the row, or `EmailOnTeam`. */
  editTeammate(personId: PersonId, request: AddTeammateRequest): Promise<EditOutcome>;
  /** Deactivate, `POST /people/{id}/deactivate` (issue 217): 204, so it resolves to nothing. */
  deactivateTeammate(personId: PersonId): Promise<void>;
  /** Reactivate, `POST /people/{id}/reactivate` (issue 218): the person's fresh pairing code. */
  reactivateTeammate(personId: PersonId): Promise<IssuedPairingCode>;
  /**
   * One person's memory topics, `GET /people/{id}/memory/topics` (knowledge-page issue 278,
   * design-spec §9.2). Any non-2xx rejects, a 404 included: the Knowledge page draws it as a
   * failed read.
   */
  memoryTopics(personId: PersonId): Promise<MemoryTopics>;
  /**
   * One page of a person's facts, `GET /people/{id}/memory/facts?` with only the query's set
   * keys (`topic`, `kind`, `page_token`) (issue 279, design-spec §9.2). Errors as `memoryTopics`.
   */
  memoryFacts(personId: PersonId, query: MemoryFactsQuery): Promise<MemoryFactsPage>;
  /**
   * One answer over a person's memory, `POST /people/{id}/memory/ask` read as server-sent
   * events (issue 282, design-spec §9.2, §9.3). Never rejects: every failure is a `failed`
   * event (a 401 calls `onUnauthorized` first), and an abort ends with no event.
   */
  ask(personId: PersonId, request: AskRequest, signal?: AbortSignal): AsyncIterable<AskEvent>;
  session(sessionId: SessionId): Promise<{ media: SessionMedia; reading: SessionReading }>;
  /** One closed recording, `GET /recordings/{id}` (issue 321): `recordingViewOf`. Non-2xx rejects. */
  recording(recordingId: RecordingId): Promise<RecordingView>;
  /** The session's knowledge from the derived route (issue 229): `KnowledgeSource.session`. */
  knowledge(sessionId: SessionId): Promise<SessionKnowledge>;
}

/** The two failures of a request the adapter reports (issue 243, `reporting/reporter.ts`). */
export type ApiFailure = "console_api_5xx" | "console_api_unreachable";

/**
 * A ceiling on the probe below, so a backend that answers every epoch cannot hold the page
 * in a loop. Far above what a 120-minute session produces.
 */
const MAX_TRACKS = 16;
const MAX_EPOCHS = 256;

/**
 * `onUnauthorized` runs on **every** 401 from a read this adapter makes (issue 118): the
 * owner-session cookie expired, was revoked, or never existed, and the Console returns to
 * sign-in rather than rendering the failure as an unreadable library. The read still rejects,
 * so no screen takes the refusal for an answer. The player's own playlist-segment and
 * `media.mp4` fetches during playback do not pass through here, so a cookie that ends
 * mid-playback shows as a player error until the next adapter read (118 code-quality P1-1).
 */
export function createBackend(
  fetchImpl: typeof fetch = fetch,
  base = API_BASE,
  onUnauthorized: () => void = () => undefined,
  onFailure: (code: ApiFailure) => void = () => undefined,
): Backend {
  // **Every request this adapter makes passes through here** (issue 243): a 5xx answer is
  // `console_api_5xx` and a fetch that rejected (no answer at all) `console_api_unreachable`,
  // handed to `onFailure` with nothing else, so the reporter never sees the URL. The answer or
  // the rejection then goes on to the caller exactly as before.
  const tracked: typeof fetch = async (input, init) => {
    let response: Response;
    try {
      response = await fetchImpl(input, init);
    } catch (error) {
      // A request the Console itself aborted (an Ask stopped, issue 282) is not an outage.
      if (init?.signal?.aborted !== true) onFailure("console_api_unreachable");
      throw error;
    }
    if (response.status >= 500) onFailure("console_api_5xx");
    return response;
  };
  function refused(what: string, status: number): BackendError {
    if (status === 401) onUnauthorized();
    return new BackendError(what, status);
  }

  async function read(what: string, path: string): Promise<Response> {
    const response = await tracked(`${base}${path}`);
    if (!response.ok) throw refused(what, response.status);
    return response;
  }

  /** One epoch's playlist text, or `null` where the epoch does not exist (404). */
  async function playlistAt(url: string): Promise<string | null> {
    const response = await tracked(url);
    if (response.status === 404) return null;
    if (!response.ok) throw refused(url, response.status);
    return response.text();
  }

  // **One derived read in flight per session.** The session view and its knowledge both come
  // from `GET /sessions/{id}/derived`, and the Console asks for both as a session opens, so a
  // read already in flight is shared. Once it settles it is forgotten: a retry is a new read.
  const inFlight = new Map<SessionId, Promise<SessionDerivedContent>>();
  function derived(sessionId: SessionId): Promise<SessionDerivedContent> {
    const shared = inFlight.get(sessionId);
    if (shared !== undefined) return shared;
    const fresh = read("GET /sessions/{id}/derived", `/sessions/${encodeURIComponent(sessionId)}/derived`)
      .then((response) => response.json() as Promise<SessionDerivedContent>)
      .finally(() => inFlight.delete(sessionId));
    inFlight.set(sessionId, fresh);
    return fresh;
  }

  return {
    // The library is `GET /recordings` since issue 321: closed recordings and legacy sessions,
    // one row each. Segments of a recording are not listed on their own.
    async library() {
      const response = await read("GET /recordings", "/recordings");
      return libraryRowsOf((await response.json()) as LibraryItem[]);
    },

    async recording(recordingId) {
      const response = await read("GET /recordings/{id}", `/recordings/${encodeURIComponent(recordingId)}`);
      return recordingViewOf((await response.json()) as RecordingDetail, base);
    },

    async knowledge(sessionId) {
      return sessionKnowledgeOf(await derived(sessionId));
    },

    async people() {
      const response = await read("GET /people", "/people");
      return (await response.json()) as PeopleRoster;
    },

    async memoryTopics(personId) {
      const response = await read(
        "GET /people/{id}/memory/topics",
        `/people/${encodeURIComponent(personId)}/memory/topics`,
      );
      return (await response.json()) as MemoryTopics;
    },

    async memoryFacts(personId, query) {
      const params = new URLSearchParams();
      if (query.topic !== null) params.set("topic", query.topic);
      if (query.kind !== null) params.set("kind", query.kind);
      if (query.page_token !== null) params.set("page_token", query.page_token);
      const response = await read(
        "GET /people/{id}/memory/facts",
        `/people/${encodeURIComponent(personId)}/memory/facts?${params}`,
      );
      return (await response.json()) as MemoryFactsPage;
    },

    // **Failures after the headers are not reported in v0** (issue 282): a stream that breaks,
    // sends malformed JSON or ends early reads as `failed` on the page and reaches no
    // `onFailure`; only the request itself passes through `tracked`.
    async *ask(personId, request, signal) {
      let response: Response;
      try {
        response = await tracked(`${base}/people/${encodeURIComponent(personId)}/memory/ask`, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "text/event-stream" },
          body: JSON.stringify(request),
          signal,
        });
      } catch {
        if (signal?.aborted !== true) yield { event: "failed" };
        return;
      }
      if (!response.ok) {
        if (response.status === 401) onUnauthorized();
        yield { event: "failed" };
        return;
      }
      if (response.body === null) {
        yield { event: "failed" };
        return;
      }
      yield* parseSse(response.body, signal);
    },

    async issuePairingCode(personId) {
      const response = await tracked(`${base}/people/${encodeURIComponent(personId)}/pairing-codes`, {
        method: "POST",
      });
      if (!response.ok) throw refused("POST /people/{id}/pairing-codes", response.status);
      return (await response.json()) as IssuedPairingCode;
    },

    async addTeammate(request) {
      const response = await tracked(`${base}/people`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!response.ok) throw refused("POST /people", response.status);
      return (await response.json()) as AddOutcome;
    },

    async editTeammate(personId, request) {
      const response = await tracked(`${base}/people/${encodeURIComponent(personId)}/edit`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!response.ok) throw refused("POST /people/{id}/edit", response.status);
      return (await response.json()) as EditOutcome;
    },

    async deactivateTeammate(personId) {
      const response = await tracked(`${base}/people/${encodeURIComponent(personId)}/deactivate`, {
        method: "POST",
      });
      if (!response.ok) throw refused("POST /people/{id}/deactivate", response.status);
    },

    async reactivateTeammate(personId) {
      const response = await tracked(`${base}/people/${encodeURIComponent(personId)}/reactivate`, {
        method: "POST",
      });
      if (!response.ok) throw refused("POST /people/{id}/reactivate", response.status);
      return (await response.json()) as IssuedPairingCode;
    },

    async session(sessionId) {
      const content = await derived(sessionId);

      // **Probed, because no owner route answers the manifest.** Epochs are numbered from 0
      // on each track, and the playlist route answers 404 for one that does not exist, so
      // the first missing epoch ends a track and a track with no epoch 0 ends the list.
      //
      // Laid end to end with no gaps, and that is a limit rather than a claim: nothing the
      // Console can read says where an epoch starts on the session timeline or where the
      // session was not recording. `width` and `height` are not on the playlist either.
      const tracks: SessionTrack[] = [];
      const epochs: SessionEpoch[] = [];
      let durationMs = 0;
      for (let track = 0; track < MAX_TRACKS; track += 1) {
        let startMs = 0;
        for (let ordinal = 0; ordinal < MAX_EPOCHS; ordinal += 1) {
          const url = `${base}/sessions/${encodeURIComponent(sessionId)}/playlist.m3u8?track=${track}&epoch=${ordinal}`;
          const text = await playlistAt(url);
          if (text === null) break;
          const mediaDurationMs = playlistMediaMs(text);
          epochs.push({
            trackIndex: track,
            ordinal,
            startMs,
            durationMs: mediaDurationMs,
            mediaDurationMs,
            playlist: url,
            width: 0,
            height: 0,
          });
          startMs += mediaDurationMs;
        }
        if (!epochs.some((epoch) => epoch.trackIndex === track)) break;
        tracks.push({ index: track, label: `Screen ${track + 1}`, kind: "video" });
        durationMs = Math.max(durationMs, startMs);
      }

      return {
        media: { sessionId, durationMs, tracks, epochs, gaps: [] },
        reading: sessionReadingOf(sessionId, content, durationMs),
      };
    },
  };
}

/**
 * An Ask answer's server-sent events as `AskEvent`s (issue 282, design-spec §9.3). Text is
 * decoded across chunks; `\r\n` and a lone `\r` end a line as `\n` does (a `\r` at a chunk's
 * end waits for the next chunk, so a split `\r\n` is one line end); a blank line ends an event.
 * Comment lines (`:`) and the `event:`, `id:` and `retry:` fields are ignored, since the tag is
 * inside the JSON; `data:` lines are joined with `\n` and parsed as one `AskEvent`, an unknown
 * `event` value skipped. After `done` or `failed` nothing more is read and the reader is
 * cancelled. Malformed JSON, or an end with no terminal event, yields `failed`. An abort (the
 * signal, or a read that rejects once it fired) ends with no event; any other read failure
 * yields `failed`. Asserted by `adapter.test.ts`.
 */
export async function* parseSse(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<AskEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let held = "";
  // A function, so each check reads the signal now rather than what an earlier check narrowed.
  const aborted = () => signal?.aborted === true;
  // Every way out cancels the reader: after a terminal event, on an abort, and when the
  // consumer stops iterating. Cancelling a stream that already ended does nothing.
  try {
    for (;;) {
      if (aborted()) return;
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch {
        if (!aborted()) yield { event: "failed" };
        return;
      }
      if (aborted()) return;
      let text = held + (chunk.done ? decoder.decode() : decoder.decode(chunk.value, { stream: true }));
      held = "";
      if (!chunk.done && text.endsWith("\r")) {
        held = "\r";
        text = text.slice(0, -1);
      }
      buffer += text.replace(/\r\n?/g, "\n");
      let end: number;
      while ((end = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        const data = block
          .split("\n")
          .filter((line) => line === "data" || line.startsWith("data:"))
          .map((line) => line.slice(5).replace(/^ /, ""));
        if (data.length === 0) continue;
        let event: AskEvent;
        try {
          event = JSON.parse(data.join("\n")) as AskEvent;
        } catch {
          yield { event: "failed" };
          return;
        }
        if (event === null || typeof event !== "object") continue;
        if (event.event === "part" || event.event === "done" || event.event === "failed") yield event;
        if (event.event === "done" || event.event === "failed") return;
      }
      // An event left unfinished at the end is dropped, as the SSE spec drops it.
      if (chunk.done) {
        yield { event: "failed" };
        return;
      }
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
}
